import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FolderKanban,
  Layers,
  Users,
  PlusCircle,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  Clock,
  ExternalLink,
  ShieldCheck,
  Star,
  Eye,
  Briefcase,
} from "lucide-react";
import { workshopsService } from "@/api/workshopsService";
import { projectsService } from "@/api/projectsService";
import { careersService } from "@/api/careersService";
import { adminAuthService } from "@/api/adminAuthService";
import { Button } from "@/components/ui/button";

/**
 * ============================================================
 * ADMIN DASHBOARD OVERVIEW (/admin)
 * ============================================================
 * Central administrative hub providing project metrics, workshop
 * enrollment statistics, quick actions, and recent activity.
 */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const adminUser = adminAuthService.getCurrentAdmin();

  const [workshops, setWorkshops] = useState([]);
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    Promise.all([
      workshopsService.getAdminWorkshops().catch(() => []),
      projectsService.getAllProjectsAdmin({ includeDeleted: false }).catch(() => []),
      careersService.getAllJobsAdmin({ includeDeleted: false }).catch(() => []),
      careersService.getApplications().catch(() => []),
    ]).then(([workshopsData, projectsData, jobsData, appsData]) => {
      if (mounted) {
        setWorkshops(workshopsData || []);
        setProjects(projectsData || []);
        setJobs(jobsData || []);
        setApplications(appsData || []);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Project Metrics
  const totalProjects = projects.length;
  const publishedProjects = projects.filter((p) => p.is_published).length;
  const draftProjects = projects.filter((p) => !p.is_published).length;
  const featuredProjects = projects.filter((p) => p.is_featured).length;

  // Career Metrics
  const totalJobs = jobs.length;
  const publishedJobs = jobs.filter((j) => j.status === "Published").length;
  const totalApplications = applications.length;
  const unreviewedApplications = applications.filter(
    (a) => a.status === "Applied" || a.status === "Under Review"
  ).length;

  // Workshop Metrics
  const totalWorkshops = workshops.length;
  const totalRegistrations = workshops.reduce(
    (sum, w) => sum + (w.registration_count || 0),
    0
  );

  return (
    <div className="space-y-8">
      {/* Top Welcome Banner */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 relative overflow-hidden shadow-sm">
        <div className="absolute right-0 top-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 text-xs font-semibold uppercase tracking-wider mb-3">
              <Sparkles className="w-3.5 h-3.5 text-cyan-600" />
              <span>Operations Control Center</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Welcome back, {adminUser?.name || "Administrator"}
            </h1>
            <p className="text-xs sm:text-sm text-slate-600 mt-1 max-w-xl font-medium">
              Monitor production case studies, manage workshop offerings, and control published public content.
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <Button
              onClick={() => navigate("/admin/projects/new")}
              className="rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold gap-2 h-10 px-5 shadow-sm"
            >
              <PlusCircle className="w-4 h-4" />
              <span>New Project</span>
            </Button>

            <Button
              onClick={() => navigate("/admin/workshops/new")}
              variant="outline"
              className="rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs font-semibold h-10 px-4"
            >
              <span>New Workshop</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Primary Metrics Grid: Projects */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-bold text-cyan-700 uppercase tracking-wider flex items-center gap-1.5">
            <FolderKanban className="w-4 h-4 text-cyan-600" />
            <span>Portfolio & Project Case Studies</span>
          </h2>
          <Link
            to="/admin/projects"
            className="text-xs text-cyan-600 hover:text-cyan-700 hover:underline font-semibold inline-flex items-center gap-1"
          >
            <span>Manage All Projects</span>
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-500 font-medium">Total Case Studies</span>
              <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
                <FolderKanban className="w-4 h-4" />
              </div>
            </div>
            <div className="text-3xl font-bold text-slate-900 tracking-tight">{totalProjects}</div>
            <div className="text-[11px] text-slate-400 mt-1 font-medium">Migrated to PostgreSQL</div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-500 font-medium">Published Live</span>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600">
                <Eye className="w-4 h-4" />
              </div>
            </div>
            <div className="text-3xl font-bold text-emerald-600 tracking-tight">{publishedProjects}</div>
            <div className="text-[11px] text-slate-400 mt-1 font-medium">Visible on public catalog</div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-500 font-medium">Draft Projects</span>
              <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
                <Clock className="w-4 h-4" />
              </div>
            </div>
            <div className="text-3xl font-bold text-amber-600 tracking-tight">{draftProjects}</div>
            <div className="text-[11px] text-slate-400 mt-1 font-medium">Pending publication</div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-500 font-medium">Featured on Home</span>
              <div className="w-8 h-8 rounded-lg bg-cyan-50 border border-cyan-200 flex items-center justify-center text-cyan-600">
                <Star className="w-4 h-4" />
              </div>
            </div>
            <div className="text-3xl font-bold text-cyan-600 tracking-tight">{featuredProjects}</div>
            <div className="text-[11px] text-slate-400 mt-1 font-medium">Hero showcase highlights</div>
          </div>
        </div>
      </div>

      {/* Career & Talent Pipeline Metrics */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-bold text-cyan-700 uppercase tracking-wider flex items-center gap-1.5">
            <Briefcase className="w-4 h-4 text-cyan-600" />
            <span>Careers & Talent Pipeline</span>
          </h2>
          <Link
            to="/admin/careers"
            className="text-xs text-cyan-600 hover:text-cyan-700 hover:underline font-semibold inline-flex items-center gap-1"
          >
            <span>Manage Careers</span>
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-500 font-medium">Active Positions</span>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600">
                <Briefcase className="w-4 h-4" />
              </div>
            </div>
            <div className="text-3xl font-bold text-emerald-600 tracking-tight">{publishedJobs}</div>
            <div className="text-[11px] text-slate-400 mt-1 font-medium">{totalJobs} total listed</div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-500 font-medium">Total Applicants</span>
              <div className="w-8 h-8 rounded-lg bg-cyan-50 border border-cyan-200 flex items-center justify-center text-cyan-600">
                <Users className="w-4 h-4" />
              </div>
            </div>
            <div className="text-3xl font-bold text-cyan-600 tracking-tight">{totalApplications}</div>
            <div className="text-[11px] text-slate-400 mt-1 font-medium">Across all positions</div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-500 font-medium">Unreviewed Applicants</span>
              <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
                <Clock className="w-4 h-4" />
              </div>
            </div>
            <div className="text-3xl font-bold text-amber-600 tracking-tight">{unreviewedApplications}</div>
            <div className="text-[11px] text-slate-400 mt-1 font-medium">Pending review</div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-500 font-medium">Workshop Enrollments</span>
              <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600">
                <Layers className="w-4 h-4" />
              </div>
            </div>
            <div className="text-3xl font-bold text-slate-900 tracking-tight">{totalRegistrations}</div>
            <div className="text-[11px] text-slate-400 mt-1 font-medium">{totalWorkshops} sessions active</div>
          </div>
        </div>
      </div>

      {/* Secondary Metrics & Quick Navigation */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Recent Projects Table Preview */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-xs">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-slate-900">Active Portfolio Case Studies</h2>
            </div>
            <Link
              to="/admin/projects"
              className="text-xs text-cyan-600 hover:text-cyan-700 hover:underline inline-flex items-center gap-1 font-semibold"
            >
              <span>View All Projects</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {loading ? (
            <div className="py-12 text-center text-xs text-slate-400 font-medium">Loading catalog...</div>
          ) : (
            <div className="space-y-2.5">
              {projects.slice(0, 5).map((p) => (
                <div
                  key={p.id}
                  className="p-3.5 rounded-xl border border-slate-100 bg-slate-50/60 hover:bg-slate-100/80 transition-colors flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-cyan-600 font-mono text-xs font-semibold shrink-0 shadow-xs">
                      {p.display_order}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs sm:text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                        <span>{p.title}</span>
                        {p.is_featured && (
                          <Star className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" />
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 font-medium">
                        <span>{p.category}</span>
                        <span>•</span>
                        <span>{p.status}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${p.is_published
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-amber-50 text-amber-700 border border-amber-200"
                        }`}
                    >
                      {p.is_published ? "Published" : "Draft"}
                    </span>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/admin/projects/${p.slug || p.id}/edit`)}
                      className="h-8 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right 1 Col: Quick Actions & Security */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-xs">
            <h2 className="text-base font-bold text-slate-900">Management Shortcuts</h2>

            <div className="space-y-2.5">
              <Button
                variant="outline"
                onClick={() => navigate("/admin/projects/new")}
                className="w-full justify-between h-11 rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs font-medium"
              >
                <div className="flex items-center gap-2.5">
                  <PlusCircle className="w-4 h-4 text-cyan-600" />
                  <span>Create New Project</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate("/admin/projects")}
                className="w-full justify-between h-11 rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs font-medium"
              >
                <div className="flex items-center gap-2.5">
                  <FolderKanban className="w-4 h-4 text-cyan-600" />
                  <span>Manage All Projects</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate("/admin/taxonomy")}
                className="w-full justify-between h-11 rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs font-medium"
              >
                <div className="flex items-center gap-2.5">
                  <Layers className="w-4 h-4 text-cyan-600" />
                  <span>Taxonomy & Technologies</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate("/admin/careers")}
                className="w-full justify-between h-11 rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs font-medium"
              >
                <div className="flex items-center gap-2.5">
                  <Briefcase className="w-4 h-4 text-cyan-600" />
                  <span>Career Management ({totalJobs})</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate("/admin/workshops")}
                className="w-full justify-between h-11 rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs font-medium"
              >
                <div className="flex items-center gap-2.5">
                  <Users className="w-4 h-4 text-cyan-600" />
                  <span>Workshop Management ({totalWorkshops})</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
              </Button>

              <Button
                variant="outline"
                onClick={() => window.open("/", "_blank")}
                className="w-full justify-between h-11 rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 text-xs font-medium"
              >
                <div className="flex items-center gap-2.5">
                  <ExternalLink className="w-4 h-4 text-cyan-600" />
                  <span>View Public Website</span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-cyan-200 bg-cyan-50/50 p-5 text-xs text-slate-700 space-y-2">
            <div className="flex items-center gap-2 text-cyan-700 font-semibold">
              <ShieldCheck className="w-4 h-4 text-cyan-600" />
              <span>Production Security Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
